import React, { useEffect } from 'react';
import { Button } from './ui/button';
import { ChevronRight, Clock } from 'lucide-react';

export const RacesSection = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-reveal, .scroll-reveal-left, .scroll-reveal-right').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // UPDATED RACES - 5km image replaced with new PNG
  const races = [
    {
      distance: '21.1KM',
      title: 'Half Marathon',
      time: '9:30 AM',
      description: 'The ultimate challenge through Camden\'s most scenic routes. Perfect for experienced runners.',
      image: '/race-half-marathon.webp',
      color: 'from-blue-600 to-blue-400',
      href: '/half-marathon'
    },
    {
      distance: '10KM',
      title: '10km Run',
      time: '10:30 AM',
      description: 'A great distance for runners of all levels. Fast, flat, and achievable through beautiful Camden.',
      image: 'https://customer-assets.emergentagent.com/job_festival-prototype/artifacts/uom6dch1_1920px_Don%20Shiel-Bicentennial-Equestrian-Park_005.jpg',
      color: 'from-orange-600 to-orange-400',
      href: '#'
    },
    {
      distance: '5KM',
      title: '5km Run',
      time: '11:30 AM',
      description: 'Perfect for beginners and families. Join the community and celebrate running together.',
      image: '/race-5km.webp',
      color: 'from-green-600 to-green-400',
      href: '#'
    },
    {
      distance: '2KM',
      title: '2km Fun Run',
      time: '12:15 PM',
      description: 'A short, fun distance for everyone. Great for first-timers and young runners.',
      image: 'https://customer-assets.emergentagent.com/job_festival-prototype/artifacts/ow1asq4m_DSC06690.JPG',
      color: 'from-purple-600 to-purple-400',
      href: '#'
    },
  ];

  return (
    <section id="races" className="section-full py-16 md:py-24 lg:py-32">
      <div className="container-wide">
        {/* Section Header */}
        <div className="mb-16 md:mb-20 scroll-reveal px-6 lg:px-0">
          <h2 className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            CHOOSE YOUR
            <br />
            DISTANCE
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl">
            Four distances, one incredible event. From beginners to experienced runners, there's a race for everyone.
          </p>
        </div>

        {/* Race Cards */}
        <div className="space-y-0">
          {races.map((race, index) => (
            <div 
              key={index}
              className={`grid lg:grid-cols-2 gap-0 border-b border-border last:border-b-0 group hover:bg-muted/50 transition-all duration-500 ${index % 2 === 0 ? 'scroll-reveal-left' : 'scroll-reveal-right'}`}
            >
              {/* Image - alternating sides on desktop, always top on mobile */}
              <div 
                className={`relative h-64 md:h-80 lg:h-96 overflow-hidden ${index % 2 === 0 ? 'lg:order-1' : 'lg:order-2'}`}
              >
                <div
                  className="absolute inset-0 bg-cover bg-center transform group-hover:scale-105 transition-transform duration-700"
                  style={{ backgroundImage: `url(${race.image})` }}
                >
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-all duration-500"></div>
                </div>
                
                {/* Distance Badge */}
                <div className="absolute top-6 left-6 md:top-8 md:left-8 z-10">
                  <div className={`bg-gradient-to-r ${race.color} px-4 py-2 md:px-6 md:py-3`}>
                    <span className="text-white font-black text-3xl md:text-4xl" style={{ fontFamily: 'Bebas Neue, cursive' }}>
                      {race.distance}
                    </span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className={`flex flex-col justify-center p-6 md:p-12 lg:p-16 ${index % 2 === 0 ? 'lg:order-2' : 'lg:order-1'}`}>
                <h3 className="text-3xl md:text-4xl lg:text-5xl font-black mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {race.title}
                </h3>
                
                <div className="flex items-center gap-2 text-muted-foreground mb-6">
                  <Clock size={18} />
                  <span className="font-bold uppercase text-sm tracking-wider">{race.time} Start</span>
                </div>
                
                <p className="text-base md:text-lg text-muted-foreground mb-8 leading-relaxed">
                  {race.description}
                </p>
                
                <div>
                  <Button asChild className="btn-standard group w-full sm:w-auto">
                    <a href={race.href}>
                      Learn more
                      <ChevronRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RacesSection;
